/* eslint-disable unicorn/consistent-function-scoping */
import { AppState, AppThunkDispatch } from '../..';
import { enqueueSnackbarAction } from '../../snackbar/actions';
import { FileTypesRecord, AdminActionNames } from './types';
import { GetTypeConfigurationRequest } from '../../../services/types';
import FileTypeConfiguration from '../../../services/file-type-configurations';
import User from '../../../services/user-access-management';
import { CSVToArray, stringToBlob } from '../../../globals/utils';
import Campaign from '../../../services/campaign';
import { response } from '../../entitlements/mock-test-data';


export const getAllFileTypes =
  () => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const input: GetTypeConfigurationRequest = {};
    const fileTypeConfiguration = new FileTypeConfiguration(
      client,
      envVariables.fileTypeConfigV2BaseUrl,
    );
    const response = await fileTypeConfiguration.getFileTypeConfigurationRecords(input);
    const { fileTypeConfigs } = response.data;
    if (fileTypeConfigs) {
      dispatch({
        type: AdminActionNames.SET_FILE_TYPE_AUTOCOMPLETE,
        payload: fileTypeConfigs,
      });
    }
  };

const hiddenFileTypeParameters = {
  fileTypeFamily: 'configuration',
  isCloudOriginated: false,
  defaultConfigValue: 'payload',
  isRegistrationRelated: false,
  isDefaultConfig: false,
};

export const getOneFileType =
  (fileId: string) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const input: GetTypeConfigurationRequest = {
      fileTypeId: fileId,
    };
    const fileTypeConfiguration = new FileTypeConfiguration(
      client,
      envVariables.fileTypeConfigV2BaseUrl,
    );
    try {
      const response = await fileTypeConfiguration.getFileTypeConfigurationRecords(input);
      const { fileTypeConfigs } = response.data;
      if (fileTypeConfigs) {
        dispatch({
          type: AdminActionNames.SET_MANAGE_FILE_TYPES,
          payload: fileTypeConfigs[0],
        });
      }
    } catch (error) {
      dispatch({
        type: AdminActionNames.SET_MANAGE_FILE_TYPES,
        payload: {
          classification: '',
          fileTypeId: '',
          isAutoSyncEnabled: false,
          extension: '',
          fileTypeName: '',
          isHashingEnabled: false,
          isCloudOriginated: false,
          isDefaultConfig: false,
          isRegistrationRelated: false,
          mastership: '',
          isOnDemandSupported: false,
          isPushEnabled: false,
          isShaSyncSupported: false,
          isSwapsEnabled: false,
          isTransferOverrideEnabled: false,
          defaultConfigValue: '',
          commandQueueLimit: '',
          fileTypeFamily: '',
        },
      });
    }
  };

export const upsertFileType =
  (fileType: FileTypesRecord, existingIds: Array<string>) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      Object.assign(fileType, hiddenFileTypeParameters);
      const fileTypeConfiguration = new FileTypeConfiguration(
        client,
        envVariables.fileTypeConfigV2BaseUrl,
      );
      try {
        if (existingIds.includes(fileType.fileTypeId)) {
          const response = await fileTypeConfiguration.editFileTypeConfiguration(fileType);
          if (response.data) {
            dispatch({
              type: AdminActionNames.SET_MANAGE_FILE_TYPES,
              payload: response.data as FileTypesRecord,
            });
            dispatch(
              enqueueSnackbarAction({
                message: `${response.data.fileTypeName} Saved`,
                options: { variant: 'success' },
              }),
            );
          }
        } else {
          const response = await fileTypeConfiguration.createFileTypeConfiguration(fileType);
          if (response.data) {
            dispatch({
              type: AdminActionNames.SET_ADD_NEW_FILE_TYPE_ID,
              payload: response.data.fileTypeId,
            });
            dispatch({
              type: AdminActionNames.SET_MANAGE_FILE_TYPES,
              payload: response.data as FileTypesRecord,
            });
            dispatch(
              enqueueSnackbarAction({
                message: `${response.data.fileTypeName} Created`,
                options: { variant: 'success' },
              }),
            );
          }
        }
      } catch (error) {
        dispatch(enqueueSnackbarAction({ message: error.message, options: { variant: 'error' } }));
      }
      return fileType as FileTypesRecord;
    };

export const searchUser = (query: string) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const userService = new User(client, envVariables.UserAccessManagementV1BaseUrl);
    try {
      const response = await userService.getUserFromCWS({
        logicalExpression: "$0 & $1",
        filters: [
          { propertyName: "username", values: [query], type: "stringEquals" },
          {
            propertyName: "userCategory", type: "stringEquals", values: ["WORKS_FOR", "APP_ENROLLED"]
          }
        ],
        responseAttributes: [
          "catrecid",
          "email",
          "username",
          "firstName",
          "lastName",
          "lastLoginTime",
          "primaryPartyDetails",
          "owner",
          "applications"
        ]
      });
      const { users } = response.data;
      if (users && users.length) {
        dispatch(
          enqueueSnackbarAction({
            message: `User is valid.`,
            options: { variant: 'success' },
          }),
        );
        return users[0];
      }
      throw new Error("User not found");
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: error.response?.data?.description || "User not found",
          options: { variant: 'error' },
        }),
      );
      return null;
    }
  };



export const updateUser = ({ catrecId, roleId = '',showMessage=true }: { catrecId: string , roleId?: string, showMessage?:boolean }) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const userService = new User(client, envVariables.UserAccessManagementV1BaseUrl);
    try {
      await userService.updateUser(catrecId, roleId);
      if(showMessage)
        dispatch(
        enqueueSnackbarAction({
          message: roleId ? `User successfully updated` : 'User successfully removed',
          options: { variant: 'success' },
        }),
      );
    } catch {
      dispatch(
        enqueueSnackbarAction({
          message: `Error while updating the user.`,
          options: { variant: 'error' },
        }),
      );
    }
    return response;
  };

export const uploadCampaignFileCSV =
  (file: any, setPercentUploaded: Function, setUploadInProgress: Function, flashType: string) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      let done = 0;
      let part = 1;
      const campaign = new Campaign(client, envVariables.campaignURL);
      const filetext = await file.text();
      const delimiter = '\r\n';
      const rows = CSVToArray(filetext, delimiter);
      const header = `${rows[0]}${delimiter}`;
      try {
        const chunkSize = 400000 / header.split(',').length;
        const incrament = rows.length / chunkSize > 0 ? (1 / (rows.length / chunkSize)) * 100 : 100;
        const chunkFileSize = 10000000 - header.length; // While javascript characters are 2-bytes, we're sending UTF-8 header by default, so characters transmitted are actually 1-byte.
        const uploadChunk = async (chunk: string) => {
          const formData = new FormData();
          formData.append(
            'fileToUpload',
            stringToBlob(chunk, 'text/csv'),
            `${file.name.toLowerCase().split('.csv')[0]}_Part${part}.csv`,
          );
          formData.append('flashType',flashType);
          const response = await campaign.uploadCampaignCSV(formData);
          done += incrament;
          setPercentUploaded(done);
          return response;
        };
        done += incrament;
        setPercentUploaded(done);
        const promises = [];
        do {
          let chunkRows = 0;
          let characterCount = 0;
          let index = 0;
          while (characterCount < chunkFileSize && index < rows.length) {
            characterCount += rows[index].length + 4;
            chunkRows += 1;
            index += 1;
          }
          if (part === 1) {
            const chunk = rows.splice(0, chunkRows).join(delimiter);
            promises.push(uploadChunk(chunk));
          } else {
            const chunk = `${header}${rows.splice(0, chunkRows).join(delimiter)}`;
            promises.push(uploadChunk(chunk));
          }
          part += 1;
        } while (chunkSize < rows.length || rows.length > 0);
        await Promise.all(promises);
        dispatch(
          enqueueSnackbarAction({
            message: `${file.name} Uploaded`,
            options: {
              variant: 'success',
            },
          }),
        );
      } catch (error: any) {
        dispatch(
          enqueueSnackbarAction({
            message: error.message,
            options: {
              variant: 'error',
            },
          }),
        );
      } finally {
        setUploadInProgress(false);
      }
    };

export const getUsersAction = () =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const userService = new User(client, envVariables.UserAccessManagementV1BaseUrl);
    try {
      const response = await userService.getUserFromCWS({
        logicalExpression: '$0 & $1',
        filters: [
          {
            propertyName: "partyNumber",
            type: "stringEquals",
            values: [
              "CAT"
            ]
          },
          {
            propertyName: "applicationId",
            type: "stringEquals",
            values: [
              "IOT"
            ]
          },
        ],
        responseAttributes: [
          "catrecid",
          "email",
          "username",
          "firstName",
          "lastName",
          "lastLoginTime",
          "applications"
        ]
      });
      const { users } = response.data;
      if (users && users.length) {
        return users;
      }
      throw new Error("User not found");
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: error.response?.data?.description || "User not found",
          options: { variant: 'error' },
        }),
      );
      return null;
    }
  };